function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/* eslint-disable max-statements */
import ContractImpl from 'web3-eth-contract';
import { sequence } from '0xsequence';
import { mainnetNetworks, testnetNetworks } from '@0xsequence/network';
import { ConnectorNotFoundError, UserRejectedRequestError } from '../errors';
import { Connector } from './base';
import { toBN } from 'web3-utils';
sequence.initWallet('polygon');
export class SequenceConnector extends Connector {
  constructor({
    chains,
    options
  }) {
    super({
      chains,
      options
    });

    _defineProperty(this, "id", 'sequence');

    _defineProperty(this, "name", 'Sequence');

    _defineProperty(this, "ready", true);

    _defineProperty(this, "provider", null);

    _defineProperty(this, "wallet", void 0);

    _defineProperty(this, "connected", false);

    _defineProperty(this, "contract", void 0);

    _defineProperty(this, "onAccountsChanged", () => {
      // sequence does not have account changed event
      return;
    });

    _defineProperty(this, "onChainChanged", chain => {
      var _this$provider;

      (_this$provider = this.provider) === null || _this$provider === void 0 ? void 0 : _this$provider.emit('chainChanged', chain);
      const id = normalizeChainId(chain);
      const unsupported = this.isChainUnsupported(id);
      this.emit('change', {
        chain: {
          id,
          unsupported
        }
      });
    });

    _defineProperty(this, "onDisconnect", () => {// no event will be emitted
    });

    this.wallet = sequence.getWallet();
  }

  async connect({
    chainId
  } = {}) {
    if (!this.wallet.isConnected()) {
      var _this$options;

      const e = await this.wallet.connect((_this$options = this.options) === null || _this$options === void 0 ? void 0 : _this$options.connect);

      if (e.error) {
        throw new UserRejectedRequestError(e.error);
      }

      if (!e.connected) {
        throw new UserRejectedRequestError('Wallet connection rejected');
      }
    }

    let id = await this.getChainId();
    const provider = await this.getProvider();
    const account = await this.getAccount();

    if (chainId && id !== chainId) {
      const chain = await this.switchChain(chainId);
      id = chain.id;
    }

    this.wallet.on('chainChanged', this.onChainChanged);
    provider.on('disconnect', this.onDisconnect);
    this.connected = true;
    return {
      account,
      chain: {
        id,
        unsupported: this.isChainUnsupported(id)
      },
      provider
    };
  }

  async disconnect() {
    this.wallet.disconnect();
  }

  getAccount() {
    return this.wallet.getAddress();
  }

  getChainId() {
    if (!this.wallet.isConnected()) {
      return this.connect().then(() => this.wallet.getChainId());
    }

    return this.wallet.getChainId();
  }

  async getProvider() {
    if (!this.provider) {
      const provider = this.wallet.getProvider();

      if (!provider) {
        throw new ConnectorNotFoundError('Failed to get Sequence Wallet provider.');
      }

      this.provider = provider;
    }

    return this.provider;
  }

  async getSigner() {
    return this.wallet.getSigner();
  }

  async isAuthorized() {
    try {
      const account = await this.getAccount();
      return !!account;
    } catch {
      return false;
    }
  }

  async switchChain(chainId) {
    var _this$provider2;

    await ((_this$provider2 = this.provider) === null || _this$provider2 === void 0 ? void 0 : _this$provider2.send('wallet_switchEthereumChain', [{
      chainId
    }]));
    return {
      id: chainId
    };
  }

  isChainUnsupported(chainId) {
    return !(mainnetNetworks.some(c => c.chainId === chainId) || testnetNetworks.some(c => c.chainId === chainId));
  }

  async getContract(config) {
    const {
      address,
      contractInterface
    } = config || {};
    const provider = await this.getProvider();
    ContractImpl.setProvider(provider); //@ts-expect-error hack because web3 library typedef doesn't match its JS implementation

    this.contract = new ContractImpl(contractInterface, address);
    return this.contract;
  }

  async signMessage(args) {
    try {
      const {
        message
      } = args;
      const signer = await this.getSigner();
      const signature = await signer.signMessage(message);
      return signature;
    } catch (error) {
      if (error.code === 4001) throw new UserRejectedRequestError(error);
      throw error;
    }
  }

  async fetchBalance(address) {
    const provider = await this.getProvider();
    const balance = await provider.request({
      method: 'eth_getBalance',
      params: [address, 'latest']
    });
    return toBN(balance === null || balance === void 0 ? void 0 : balance.toString());
  }

  async watchAsset({
    address,
    decimals = 18,
    image,
    symbol
  }) {
    const provider = await this.getProvider();
    return await provider.request({
      method: 'wallet_watchAsset',
      params: [{
        type: 'ERC20',
        options: {
          address,
          decimals,
          image,
          symbol
        }
      }]
    });
  }

}

function normalizeChainId(chainId) {
  if (typeof chainId === 'string') return Number.parseInt(chainId, chainId.trim().substring(0, 2) === '0x' ? 16 : 10);
  if (typeof chainId === 'bigint') return Number(chainId);
  return chainId;
}